import type moment from 'moment'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import {
  useDefaultAttributesFromRows,
  useDefaultAttributesFromRowsGraphQL,
} from 'modules/shift_management/hooks/useDefaultAttributesFromRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import CommonSelectorsForm from 'modules/shift_management/lib/forms/CommonSelectorsForm'
import { Leave } from 'modules/shift_management/lib/types'

import useDefaultValues from './useDefaultValues'

export interface Props {
  date?: moment.Moment
  leave?: Leave
  form: CommonSelectorsForm
}

const useCommonSelectors = ({ date, leave, form }: Props) => {
  const [{ view }] = useSchedule()
  const { daysByRow } = useSelectionState()

  // When daysByRow are from work area view, they have work area ids and not employee ids
  // so we need to start with an empty array for the employee selector to work correctly
  const selectedEmployeesIds = Object.keys(daysByRow)
  const getIds = selectedEmployeesIds.map((id) => Number(id))
  const employeeIds = !!selectedEmployeesIds.length && view.isByEmployees ? getIds : []

  form.setValues({ employee_id: employeeIds.length ? employeeIds[0] : undefined })
  useUnsafeEffect(() => {
    form.employeeSelection = {
      type: 'manual',
      ids: employeeIds,
    }
  }, [])

  const { loading: loadingEmployees, selectableEmployeeIds } = useSelectableEmployees(
    view.startAt,
    view.endAt
  )
  const defaultAttributes = useDefaultAttributesFromRows()
  const defaultAttributesGraphQL = useDefaultAttributesFromRowsGraphQL()

  const { weekDays, daysByEmployee } = useDefaultValues(
    form.employeeSelection?.type === 'all',
    form.employeeSelection?.ids ?? [],
    form,
    date,
    leave
  )

  return {
    /**
     * @deprecated Use defaultAttributesGraphQL instead, which works using mutations.
     */
    defaultAttributes,
    defaultAttributesGraphQL,
    loadingEmployees,
    selectableEmployeeIds,
    weekDays,
    daysByEmployee,
  }
}

export default useCommonSelectors
